import { useRef, useState } from 'react';
import {
  Button,
  Fieldset,
  Grid,
  GridCol,
  Heading,
  Input,
  Popup,
  PopupSection,
  Tooltip,
} from '@flixbus/honeycomb-react';
import { Icon, IconCalendar, IconCrossed } from '@flixbus/honeycomb-icons-react';

import { useBatchUpdate } from '../../context/VoucherBatchUpdateProvider';
import { useNotification } from '../Notifications/Context';

import { fetchVoucherBatch } from '../../apis';
import { DateTime } from 'luxon';
import { toISODate } from '../../helpers/dateTime';
import { useVoucherList } from '../../context/VoucherListProvider';

const ChangeExpiration = () => {
  const [popupActive, setPopupActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const { pushToast } = useNotification();
  const { payload, handleUpdate, clearSelection } = useBatchUpdate();
  const targetRef = useRef(null);
  const { refetch } = useVoucherList();

  const [localValidSince, setLocalValidSince] = useState(
    payload.batches[0]?.query?.validityInterval?.validSince || '',
  );
  const [localValidUntil, setLocalValidUntil] = useState(
    payload.batches[0]?.query?.validityInterval?.validUntil || '',
  );

  const vouchersCount = payload.batches[0].ids.length;

  const handleDateChange = (prop: 'validSince' | 'validUntil', value: string) => {
    if (!value) return;

    if (prop === 'validSince') {
      if (localValidUntil && DateTime.fromISO(value) >= DateTime.fromISO(localValidUntil)) {
        return;
      }
      setLocalValidSince(value);
    } else {
      if (DateTime.fromISO(value) <= DateTime.fromISO(localValidSince)) {
        return;
      }
      setLocalValidUntil(value);
    }

    handleUpdate({
      prop: 'validityInterval',
      value: {
        ...payload.batches[0]?.query?.validityInterval,
        [prop]: toISODate(DateTime.fromISO(value)),
      },
    });
  };

  const onSubmit = () => {
    setPopupActive(true);
  };

  const onConfirm = async () => {
    setLoading(true);
    const query = { ...payload };
    await fetchVoucherBatch(query);
    setLoading(false);
    setPopupActive(false);
    pushToast({
      message: `${vouchersCount} voucher(s) were successfully changed`,
      status: 'success',
    });

    setTimeout(() => {
      refetch();
      clearSelection();
    }, 1000);
  };

  return (
    <>
      <div>
        <Tooltip
          id='language-selection'
          size='large'
          position='bottom'
          alignment='start'
          content={
            <Fieldset itemSpacing='half'>
              <Grid>
                <GridCol>
                  <Input
                    name='date-field-validSince'
                    id='date-field-validSince'
                    label='Voucher valid from'
                    type='date'
                    min={new Date().toISOString().split('T')[0]}
                    required
                    value={localValidSince}
                    onChange={e => handleDateChange('validSince', e.target.value)}
                  />
                </GridCol>
                <GridCol>
                  <Input
                    name='date-field-validUntil'
                    id='date-field-validUntil'
                    label='Voucher valid until'
                    type='date'
                    min={new Date().toISOString().split('T')[0]}
                    required
                    value={localValidUntil}
                    onChange={e => handleDateChange('validUntil', e.target.value)}
                  />
                </GridCol>
              </Grid>

              <Button onClick={onSubmit} appearance='primary'>
                Submit
              </Button>
            </Fieldset>
          }
        >
          <Button appearance='link' disabled={vouchersCount === 0}>
            <Icon InlineIcon={IconCalendar} />
            Change Expiration
          </Button>
        </Tooltip>
      </div>

      <Popup
        active={popupActive}
        onOverlayClick={() => setPopupActive(false)}
        targetRef={targetRef}
      >
        <PopupSection type='icon'>
          <Icon InlineIcon={IconCrossed} size={12} style={{ fill: 'var(--hcr-danger-color)' }} />
        </PopupSection>
        <PopupSection type='title'>
          <Heading size={2} sectionHeader flushSpace>
            Change Expiration Date?
          </Heading>
        </PopupSection>
        <PopupSection type='content'>
          You are about to change the expiration date for {vouchersCount} vouchers from{' '}
          {DateTime.fromISO(localValidSince).toFormat('dd-MM-yyyy')} to{' '}
          {DateTime.fromISO(localValidUntil).toFormat('dd-MM-yyyy')}. Make sure you selected the
          correct vouchers and the changes are correct before continuing.
        </PopupSection>
        <PopupSection type='actions'>
          <Button onClick={() => setPopupActive(false)}>Cancel</Button>
          <Button appearance='primary' onClick={onConfirm} loading={loading}>
            Confirm
          </Button>
        </PopupSection>
      </Popup>
    </>
  );
};

export default ChangeExpiration;
